import { Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React, { useEffect } from "react";
import { useGlobal } from "../../../contexts/globalContext";

import * as S from "./styles";
import CivitatisCard from "./CivitatisCard";
import EntertainmentLoader from "./EntertainmentLoader";

const useStyles = makeStyles(() => ({
	header: { marginBottom: 16, paddingLeft: 4 },
	headerTitle: { fontSize: 22, fontWeight: 700, color: "#1a1a2e", lineHeight: 1.3 },
	headerSubtitle: { fontSize: 14, color: "#7f7f7f", marginTop: 4 },
}));

const DURATION_INFINITY = 9999999;

const STOPWORDS = new Set(["de", "do", "da", "dos", "das", "e", "a", "o", "os", "as", "no", "na", "nos", "nas", "em", "por", "para", "com", "ao", "à", "um", "uma"]);

const normalize = (str) =>
	str.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const getKeywords = (categoryLabel) =>
	normalize(categoryLabel)
		.split(/\s+/)
		.filter((w) => w.length > 2 && !STOPWORDS.has(w));

const EntertainmentList = ({ filters, loading, error, searchState }) => {
	const { setLoading } = useGlobal();
	const classes = useStyles();

	const applyFilters = () => {
		if (!searchState?.activities) return [];

		return searchState.activities.filter((activity) => {
			// Categories — keyword search against activity title
			if (filters?.categories?.length) {
				const titleNorm = normalize(activity.title || activity.name || "");
				const matched = filters.categories.some((cat) =>
					getKeywords(cat).some((kw) => titleNorm.includes(kw))
				);
				if (!matched) return false;
			}

			// Characteristics
			const chars = filters?.characteristics || {};
			if (chars.freeCancellation) {
				const hasFreeCancel = Array.isArray(activity.cancelPolicies)
						? activity.cancelPolicies.some((p) => p.penalty === 0 || p.penalty === 0.0)
						: activity.daysBeforeCancellationPolicy > 0;
				if (!hasFreeCancel) return false;
			}
			if (chars.portuguese) {
				const langs = activity.guideLanguages || [];
				if (!langs.some((l) => /pt|por/i.test(l))) return false;
			}
			if (chars.accessible) {
				if (!activity.accessible && !activity.wheelchairAccessible) return false;
			}
			if (chars.hotelPickup) {
				if (!activity.hotelPickup) return false;
			}

			// Start hour (skip if at 0 — default)
			if (filters?.startHour > 0 && activity.startTime) {
				const actHour = parseInt(activity.startTime.split(":")[0], 10);
				if (actHour < filters.startHour) return false;
			}

			// Price range (in points)
			if (filters?.priceRange && activity.pointsPrice != null) {
				const pts = Number(activity.pointsPrice);
				if (pts < filters.priceRange[0] || pts > filters.priceRange[1]) return false;
			}

			// Duration
			if (filters?.durationOption) {
				const durationMin = activity.duration?.min || 0;
				const { min, max } = filters.durationOption;
				const effectiveMax = max === Infinity ? DURATION_INFINITY : max;
				if (durationMin < min || durationMin > effectiveMax) return false;
			}

			return true;
		});
	};

	const filteredActivities = applyFilters();
	const destination = searchState?.selectedDestination;

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		setLoading(loading);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading]);

	if (loading) {
		return (
			<Grid container>
				<Grid item sm={12} md={12} lg={12}>
					<S.Container>
						<EntertainmentLoader />
					</S.Container>
				</Grid>
			</Grid>
		);
	}

	return (
		<>
			{destination?.name && (
				<div className={classes.header}>
					<Typography className={classes.headerTitle}>
						Experiências em {destination.name}
					</Typography>
					<Typography className={classes.headerSubtitle}>
						{filteredActivities.length} atividade{filteredActivities.length !== 1 ? "s" : ""} encontrada{filteredActivities.length !== 1 ? "s" : ""} na região
					</Typography>
				</div>
			)}
			{!destination?.name && (
				<S.CounterText>
					<span>{filteredActivities.length} Resultados encontrados</span>
				</S.CounterText>
			)}
			{filteredActivities.map((activity) => {
				const key = activity.id || activity.activityId;
				return (
					<S.Wrapper className="wrapperList" key={key}>
						<CivitatisCard activity={activity} />
					</S.Wrapper>
				);
			})}
		</>
	);
};

export default EntertainmentList;
